/* eslint-disable ember/no-mixins */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import ReportingReviewMixin from 'linced-app/mixins/reporting-review';
import ReportingBaseMixin from 'linced-app/mixins/reporting-base';

export default class ReviewController extends Controller.extend(
  ReportingReviewMixin,
  ReportingBaseMixin,
) {
  @service
  router;

  // Declared so the param binds reliably rather than relying on the route's
  // queryParams hash alone. Only periodIndex is listed — `mode` keeps its
  // existing route-level mechanism so its URL behaviour is unchanged.
  queryParams = ['periodIndex', 'periodEndDate', 'periodYear'];
  @tracked periodIndex = null;
  // The period's end date, which addresses it across template versions where
  // an index cannot. Set by the group cards; absent for an orphaned period.
  @tracked periodEndDate = null;
  // The academic year the period belongs to. Set by historic cards, which can
  // sit in a past year; absent means the current one.
  @tracked periodYear = null;

  @service
  studentReportActions;

  @computed('parentController.editorOptions')
  get editorOptions() {
    return this.parentController.get('editorOptions');
  }

  @computed('parentController.progressChartColors')
  get progressChartColors() {
    return this.parentController.get('progressChartColors');
  }

  @alias('groupController.activeReport')
  activeReport;

  isSingleStudent = false;

  // "group - template - year - period" header line, built by the route so it
  // names the period actually being reviewed. Separate from the template
  // record's displayName, which is shared with screens that aren't
  // group-scoped.
  @tracked reviewHeading = null;

  // Group-specific state
  @tracked
  reviewReportsOpen = false;

  @tracked
  reviewStudents = null;
  @tracked
  _captureReportVisuals = null;
  @tracked reviewSelectedStudent;
  @tracked reviewEditingSection;
  @tracked reviewEditingComponent;
  @tracked reviewProgressSubject;
  @tracked reviewProgressLevels;
  @tracked reviewProgressExpectations;
  @tracked reviewProgressRecordsData;
  @tracked reviewProgressRecordsLoading;
  @tracked reviewStrandSubject;
  @tracked reviewStrandProgressComponents;
  @tracked _reviewRefreshFn;
  @tracked rubricDialogStudent;
  @tracked showRubricDialog;

  // All reports still in the review stage, as an array — drives the bulk
  // "Download all" button. Derived from reviewStudents so it shrinks as
  // reports are marked ready to publish (which removes them from reviewStudents).
  @computed('reviewStudents.[]', 'reviewStudentReports')
  get reviewAllStudentReports() {
    let students = this.reviewStudents || [];
    let reportMap = this.reviewStudentReports || {};
    return students.map((s) => reportMap[s.get('id')]).filter(Boolean);
  }

  cleanupReviewView() {
    this._cleanupReviewState();
    this.reviewHeading = null;
    this.reviewReportsOpen = false;
    this.reviewStudents = null;
  }

  @action
  exitReviewReports() {
    let groupCtrl = this.groupController;
    this.cleanupReviewView();
    groupCtrl.set('activeReport', null);
    this.router.transitionTo('reporting.group');
  }

  @action
  selectReviewStudent(student) {
    this.reviewSelectedStudent = student;
    this.reviewEditingSection = null;
    this.reviewEditingComponent = null;
    this.reviewProgressSubject = null;
    this.reviewProgressLevels = null;
    this.reviewProgressExpectations = null;
    this.reviewProgressRecordsData = null;
    this.reviewProgressRecordsLoading = false;
    this.reviewStrandSubject = null;
    this.reviewStrandProgressComponents = null;
  }

  @action
  reviewSelectComponent(section, component) {
    this._selectReviewComponent(section, component);
  }

  @action
  closeReviewEditor() {
    this._closeReviewEditor();
  }

  @action
  async updateReviewProgressRecords() {
    let data = this.reviewProgressRecordsData;
    if (!data) {
      return;
    }
    await data.update().catch(() => null);
  }

  @action
  reviewUpdateJudgement(elementId, value) {
    this._updateReviewStrand('judgement', elementId, value);
  }

  @action
  reviewUpdateEffort(elementId, value) {
    this._updateReviewStrand('effort', elementId, value);
  }

  @action
  updateReviewField(value) {
    this._updateReviewField(value);
  }

  @action
  updateReviewNextStepsGoals(value) {
    this._updateReviewField(value, 'next-steps-goals');
  }

  @action
  registerReportViewCapture(fn) {
    this._captureReportVisuals = fn;
  }

  @action
  registerReviewRefresh(fn) {
    this._reviewRefreshFn = fn;
  }

  @action
  openRubricDialog(student) {
    this.rubricDialogStudent = student;
    this.showRubricDialog = true;
  }

  @action
  closeRubricDialog() {
    this.showRubricDialog = false;
    this.rubricDialogStudent = null;
  }

  @action
  async markReadyToPublish() {
    let student = this.reviewSelectedStudent;
    if (!student) return;
    let studentReport = this.reviewStudentReports[student.get('id')];
    if (!studentReport) return;

    if (this._captureReportVisuals) {
      await this._captureReportVisuals();
    }

    try {
      await this.studentReportActions.approve(studentReport);
    } catch (e) {
      console.error('Failed to approve report for publishing', e);
      return;
    }

    this.reviewStudents = this.reviewStudents.filter((s) => s !== student);

    if (this.reviewStudents.length) {
      this.reviewSelectedStudent = this.reviewStudents[0];
    } else {
      this.reviewSelectedStudent = null;
    }
  }
}
